<script setup lang="ts">
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue';
import { CheckIcon, SelectorIcon } from '@heroicons/vue/solid';
import type { PropType } from 'vue';
import type { Collection } from '~/types/store';
import { getApiRoot } from '~/utilities/api';

const props = defineProps({
  profile: { type: Object as PropType<any | undefined>, required: true },
  additionalCollections: { type: Array as PropType<Collection[]>, default: () => [] },
  modelValue: { type: Object as PropType<any | undefined>, required: true },
  required: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const { modelValue: selectedCollection, profile, additionalCollections } = toRefs(props);

const truthyFilter = <T>(x: T | false | undefined | '' | 0): x is T => !!x;

const collections = ref<Collection[] | undefined>(undefined);
watch(
  profile,
  async (newProfile) => {
    if (newProfile) {
      collections.value = undefined;
      try {
        const { items } = await $fetch<{ items: any[] }>(`${getApiRoot()}/profile/${newProfile.id}/collections`);
        const collectionMetadata = await Promise.all(
          items.map((collection) =>
            $fetch<{
              metadata: {
                id: string;
                name: string;
                attributes: {
                  type: string;
                  value: string;
                }[];
              };
              license_uris: string[];
              license_hash: string;
            }>(`${getApiRoot()}/collections/${collection.id}/metadata`).catch(() => undefined)
          )
        );
        collections.value = collectionMetadata.filter(truthyFilter).map(({ metadata, license_uris, license_hash }) => {
          const description = metadata.attributes.find((attribute) => attribute.type === 'description')?.value;
          const iconUrl = metadata.attributes.find((attribute) => attribute.type === 'icon')?.value;
          const bannerUrl = metadata.attributes.find((attribute) => attribute.type === 'banner')?.value;
          const twitterHandle = metadata.attributes.find((attribute) => attribute.type === 'twitter')?.value;
          const website = metadata.attributes.find((attribute) => attribute.type === 'website')?.value;

          const licenseUrl = license_uris[0];
          const licenseHash = license_hash;
          return {
            id: metadata.id,
            name: metadata.name,
            description,
            iconUrl,
            bannerUrl,
            twitterHandle,
            website,
            licenseUrl,
            licenseHash,
            attributes: [],
          };
        });
      } catch (e) {
        collections.value = [];
      }
    }
  },
  { immediate: true }
);
</script>
<template>
  <div class="relative dark:text-white">
    <Listbox
      as="div"
      :model-value="modelValue"
      @update:modelValue="emit('update:modelValue', $event)"
      :disabled="collections === undefined"
    >
      <div class="relative mt-1">
        <ListboxButton
          class="sm:text-md relative w-full min-w-[20rem] cursor-default rounded-md border border-neutral-300 bg-white py-4 pl-4 pr-12 text-left shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:bg-black"
        >
          <span v-if="selectedCollection" class="flex items-center">
            <img v-if="selectedCollection.iconUrl" class="h-8 w-8 rounded-full" :src="selectedCollection.iconUrl" />
            <span :class="selectedCollection.iconUrl ? 'ml-3' : ''" class="block truncate">{{
              selectedCollection.name
            }}</span>
          </span>
          <span v-else-if="collections === undefined" class="flex items-center">
            <span class="block truncate">Loading...</span>
          </span>
          <span v-else class="flex items-center">
            <span class="block truncate">No Collection</span>
          </span>
          <span class="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
            <SelectorIcon class="h-5 w-5 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
          </span>
        </ListboxButton>

        <transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <ListboxOptions
            class="sm:text-md absolute z-10 mt-2 max-h-72 w-full overflow-auto rounded-md bg-white p-2 text-base shadow-lg ring-1 ring-neutral-300 focus:outline-none dark:bg-black dark:ring-neutral-600"
          >
            <ListboxOption as="template" :key="undefined" :value="undefined" v-slot="{ active, selected }">
              <li
                :class="[
                  active ? 'bg-neutral-100 dark:bg-neutral-800' : 'text-neutral-900 dark:text-neutral-50',
                  'relative cursor-default select-none rounded-md py-3 pl-4 pr-4',
                ]"
              >
                <div class="flex items-center">
                  <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']"> No Collection </span>
                </div>

                <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4 text-emerald-600']">
                  <CheckIcon class="h-5 w-5" aria-hidden="true" />
                </span>
              </li>
            </ListboxOption>
            <ListboxOption
              as="template"
              v-for="collection in [...(collections || []), ...additionalCollections]"
              :key="collection.id"
              :value="collection"
              v-slot="{ active, selected }"
            >
              <li
                :class="[
                  active ? 'bg-neutral-100 dark:bg-neutral-800' : 'text-neutral-900 dark:text-neutral-50',
                  'relative cursor-default select-none rounded-md py-3 pl-4 pr-4',
                ]"
              >
                <div class="flex items-center">
                  <img v-if="collection.iconUrl" class="h-8 w-8 rounded-full" :src="collection.iconUrl" />
                  <span :class="[selected ? 'font-semibold' : 'font-normal', 'ml-3 block truncate']">
                    {{ collection.name }}
                  </span>
                </div>

                <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4 text-emerald-600']">
                  <CheckIcon class="h-5 w-5" aria-hidden="true" />
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>
    <div class="isolate select-none opacity-0" aria-hidden="true" tabIndex="{-1}">
      <div class="absolute bottom-0 left-0 z-10 h-[1px] w-full opacity-0" aria-hidden="true" tabIndex="{-1}"></div>
      <select
        tabindex="-1"
        :value="selectedCollection?.id"
        name="collection"
        :required="required"
        aria-hidden="true"
        autoCapitalize="off"
        autoComplete="off"
        class="absolute bottom-0 left-0 z-0 h-[1px] w-full select-none appearance-none bg-transparent p-0 text-transparent opacity-0 shadow-none !outline-none"
      >
        <option value="" aria-hidden="true"></option>
        <option
          v-for="collection in [...(collections || []), ...additionalCollections]"
          :key="collection.id"
          :value="collection.id"
          aria-hidden="true"
        >
          {{ collection.name }}
        </option>
      </select>
    </div>
  </div>
</template>
